import React, { useState } from 'react';

import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material';

import type { ClaimCampaign, ClaimCsvRow } from '~/types/loyaltyContract';

interface Props {
  campaign: ClaimCampaign;
  onBack: () => void;
  onProceedToReview: (rows: Array<ClaimCsvRow>, options?: { enrolMember?: boolean }) => void;
}

export default function SingleClaimStep({ campaign, onBack, onProceedToReview }: Props) {
  const [customerId, setCustomerId] = useState('');
  const [code, setCode] = useState('');
  const [enrolMember, setEnrolMember] = useState(false);

  const requiresCode = campaign.validatorType === 'PREGEN_CODE' || campaign.validatorType === 'SHARED_CODE';

  const handleProceedToReview = () => {
    if (!customerId.trim()) return;
    if (requiresCode && !code.trim()) return;
    onProceedToReview([{ customerId: customerId.trim(), ...(code.trim() ? { code: code.trim() } : {}) }], {
      enrolMember,
    });
  };

  const canProceed = !!customerId.trim() && (!requiresCode || !!code.trim());

  return (
    <Box>
      <Grid container spacing={3} columns={12}>
        {/* Left column - Form */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Claim Details
              </Typography>
              <Stack spacing={2} mt={1}>
                <TextField
                  label="Customer ID"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
                {requiresCode && (
                  <TextField
                    label="Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                )}
              </Stack>
            </Box>

            <Box
              sx={{
                bgcolor: enrolMember ? 'rgba(2, 136, 209, 0.08)' : 'action.hover',
                border: 1,
                borderColor: enrolMember ? 'info.main' : 'divider',
                borderRadius: 1,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onClick={() => setEnrolMember((prev) => !prev)}
            >
              <FormControlLabel
                control={<Checkbox checked={enrolMember} />}
                label={
                  <Box>
                    <Typography variant="subtitle2">Enrol customer into Société</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Customer will be automatically added to the loyalty program if not yet enrolled
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>
          </Stack>
        </Grid>

        {/* Right column - Summary */}
        <Grid size={{ xs: 12, md: 8 }}>
          {customerId.trim() ? (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Claim Summary
              </Typography>
              <Typography variant="body2">
                Customer <strong>{customerId.trim()}</strong> will be claimed for campaign{' '}
                <strong>{campaign.name}</strong>
              </Typography>
              {code.trim() && (
                <Typography variant="body2" mt={1}>
                  Code: <strong>{code.trim()}</strong>
                </Typography>
              )}
              {enrolMember && (
                <Typography variant="body2" mt={1} color="info.main">
                  Customer will be enrolled into Société before claiming
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Typography color="text.secondary">Enter a customer ID to continue</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Bottom bar */}
      <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3} pt={2} borderTop={1} borderColor="divider">
        <Button variant="text" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" onClick={handleProceedToReview} disabled={!canProceed}>
          Proceed to Review
        </Button>
      </Stack>
    </Box>
  );
}
